import React from 'react';
import { motion } from 'framer-motion';
import { PaletteIcon, UploadIcon, SparklesIcon, LayersIcon } from 'lucide-react';
export function JerseyBuilder() {
  return <section className="relative py-24 sm:py-32 overflow-hidden" data-id="element-61">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950" data-id="element-62" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-63">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-64">
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-white mb-4" data-id="element-65">
            FORGE YOUR{' '}
            <span className="text-cyan-400 text-glow-cyan" data-id="element-66">COLORS</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-67">
            Design pro-grade team jerseys in minutes. Drag, drop, dominate.
          </p>
        </motion.div>

        {/* Jersey Builder Mockup */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative" data-id="element-68">
          <div className="bg-gray-900/80 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-4 sm:p-8 glow-cyan" data-id="element-69">
            {/* Mockup Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800" data-id="element-70">
              <div className="flex items-center gap-3" data-id="element-71">
                <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-72" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-73" />
                <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-74" />
              </div>
              <span className="text-sm text-gray-500 font-mono" data-id="element-75">
                jersey-builder.clanarena.io
              </span>
              <div className="w-20" data-id="element-76" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-77">
              {/* Jersey Preview */}
              <div className="relative flex items-center justify-center p-8 bg-gray-950/50 rounded-xl border border-gray-800" data-id="element-78">
                {/* Jersey SVG Mockup */}
                <svg viewBox="0 0 200 240" className="w-full max-w-xs" data-id="element-79">
                  {/* Jersey body */}
                  <defs data-id="element-80">
                    <linearGradient id="jerseyGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-81">
                      <stop offset="0%" stopColor="#0f172a" data-id="element-82" />
                      <stop offset="50%" stopColor="#1e293b" data-id="element-83" />
                      <stop offset="100%" stopColor="#0f172a" data-id="element-84" />
                    </linearGradient>
                    <linearGradient id="accentGradient" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-85">
                      <stop offset="0%" stopColor="#22d3ee" data-id="element-86" />
                      <stop offset="100%" stopColor="#06b6d4" data-id="element-87" />
                    </linearGradient>
                  </defs>

                  {/* Main body */}
                  <path d="M40 60 L20 80 L20 200 L80 220 L120 220 L180 200 L180 80 L160 60 L140 40 L60 40 Z" fill="url(#jerseyGradient)" stroke="#22d3ee" strokeWidth="2" data-id="element-88" />

                  {/* Left sleeve */}
                  <path d="M40 60 L10 90 L10 130 L20 140 L20 80 L40 60" fill="url(#jerseyGradient)" stroke="#22d3ee" strokeWidth="2" data-id="element-89" />

                  {/* Right sleeve */}
                  <path d="M160 60 L190 90 L190 130 L180 140 L180 80 L160 60" fill="url(#jerseyGradient)" stroke="#22d3ee" strokeWidth="2" data-id="element-90" />

                  {/* Collar */}
                  <path d="M60 40 L80 55 L100 60 L120 55 L140 40" fill="none" stroke="#22d3ee" strokeWidth="2" data-id="element-91" />

                  {/* Accent stripes */}
                  <rect x="25" y="85" width="4" height="100" fill="url(#accentGradient)" data-id="element-92" />
                  <rect x="171" y="85" width="4" height="100" fill="url(#accentGradient)" data-id="element-93" />

                  {/* Team name */}
                  <text x="100" y="120" textAnchor="middle" fill="#22d3ee" fontSize="14" fontWeight="bold" fontFamily="Orbitron, sans-serif" data-id="element-94">
                    CLANARENA
                  </text>

                  {/* Player name */}
                  <text x="100" y="160" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold" fontFamily="Orbitron, sans-serif" data-id="element-95">
                    VORTEX
                  </text>

                  {/* Number */}
                  <text x="100" y="195" textAnchor="middle" fill="white" fontSize="32" fontWeight="bold" fontFamily="Orbitron, sans-serif" data-id="element-96">
                    07
                  </text>
                </svg>

                {/* Floating badges */}
                <motion.div animate={{
                y: [-5, 5, -5]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="absolute top-4 right-4 px-3 py-1 bg-lime-500/20 border border-lime-500/50 rounded-full text-lime-400 text-xs font-bold uppercase" data-id="element-97">
                  Preview
                </motion.div>
              </div>

              {/* Controls Panel */}
              <div className="space-y-6" data-id="element-98">
                {/* Color Picker */}
                <div className="bg-gray-950/50 rounded-xl border border-gray-800 p-4" data-id="element-99">
                  <div className="flex items-center gap-2 mb-4" data-id="element-100">
                    <PaletteIcon className="w-5 h-5 text-cyan-400" data-id="element-101" />
                    <span className="font-bold text-white uppercase tracking-wider text-sm" data-id="element-102">
                      Team Colors
                    </span>
                  </div>
                  <div className="flex gap-3" data-id="element-103">
                    <div className="w-10 h-10 rounded-lg bg-cyan-500 border-2 border-white cursor-pointer" data-id="element-104" />
                    <div className="w-10 h-10 rounded-lg bg-gray-900 border border-gray-700 cursor-pointer" data-id="element-105" />
                    <div className="w-10 h-10 rounded-lg bg-fuchsia-500 border border-gray-700 cursor-pointer" data-id="element-106" />
                    <div className="w-10 h-10 rounded-lg bg-lime-500 border border-gray-700 cursor-pointer" data-id="element-107" />
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-fuchsia-500 border border-gray-700 cursor-pointer" data-id="element-108" />
                  </div>
                </div>

                {/* Pattern Selector */}
                <div className="bg-gray-950/50 rounded-xl border border-gray-800 p-4" data-id="element-109">
                  <div className="flex items-center gap-2 mb-4" data-id="element-110">
                    <LayersIcon className="w-5 h-5 text-cyan-400" data-id="element-111" />
                    <span className="font-bold text-white uppercase tracking-wider text-sm" data-id="element-112">
                      Patterns
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2" data-id="element-113">
                    <div className="aspect-square rounded-lg bg-gray-800 border-2 border-cyan-500 cursor-pointer" data-id="element-114" />
                    <div className="aspect-square rounded-lg bg-gray-800 border border-gray-700 cursor-pointer flex items-center justify-center" data-id="element-115">
                      <div className="w-full h-0.5 bg-gray-600 rotate-45" data-id="element-116" />
                    </div>
                    <div className="aspect-square rounded-lg bg-gray-800 border border-gray-700 cursor-pointer flex items-center justify-center" data-id="element-117">
                      <div className="grid grid-cols-2 gap-0.5 w-4 h-4" data-id="element-118">
                        <div className="bg-gray-600" data-id="element-119" />
                        <div className="bg-gray-700" data-id="element-120" />
                        <div className="bg-gray-700" data-id="element-121" />
                        <div className="bg-gray-600" data-id="element-122" />
                      </div>
                    </div>
                    <div className="aspect-square rounded-lg bg-gray-800 border border-gray-700 cursor-pointer flex items-center justify-center" data-id="element-123">
                      <SparklesIcon className="w-4 h-4 text-gray-600" data-id="element-124" />
                    </div>
                  </div>
                </div>

                {/* Logo Upload */}
                <div className="bg-gray-950/50 rounded-xl border border-gray-800 border-dashed p-6 text-center cursor-pointer hover:border-cyan-500/50 transition-colors" data-id="element-125">
                  <UploadIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" data-id="element-126" />
                  <span className="text-sm text-gray-500" data-id="element-127">
                    Drop your logo here
                  </span>
                </div>

                {/* Price */}
                <div className="text-center pt-4" data-id="element-128">
                  <span className="text-gray-400" data-id="element-129">Starting at </span>
                  <span className="font-display font-bold text-2xl text-lime-400" data-id="element-130">
                    €14.99
                  </span>
                  <span className="text-gray-400" data-id="element-131"> per jersey</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}